import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import shortid from 'shortid';
import './Tooltip.css';

const Tooltip = ({
  className = '',
  id = '',
  trigger,
  content,
  on = 'hover',
  position = 'bottom center',
  inverted = false,
  ...otherProps
}) => {

  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipId = id || `tooltip-${shortid.generate()}`;

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const spacing = 8; // Space between trigger and tooltip

    let top = 0;
    let left = 0;

    // Calculate position based on the position prop
    switch (position) {
      case 'top center':
        top = triggerRect.top - tooltipRect.height - spacing;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'top left':
        top = triggerRect.top - tooltipRect.height - spacing;
        left = triggerRect.left;
        break;
      case 'top right':
        top = triggerRect.top - tooltipRect.height - spacing;
        left = triggerRect.right - tooltipRect.width;
        break;
      case 'bottom center':
        top = triggerRect.bottom + spacing;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom left':
        top = triggerRect.bottom + spacing;
        left = triggerRect.left;
        break;
      case 'bottom right':
        top = triggerRect.bottom + spacing;
        left = triggerRect.right - tooltipRect.width;
        break;
      case 'left center':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - spacing;
        break;
      case 'right center':
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + spacing;
        break;
      default:
        // Default to bottom center
        top = triggerRect.bottom + spacing;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
    }

    // Adjust position to keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < 0) left = spacing;
    if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - spacing;
    }
    if (top < 0) top = spacing;
    if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - spacing;
    }

    setTooltipPosition({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
      
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [isVisible]);

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);

  const handleClick = (e) => {
    if (on === 'click') {
      e.stopPropagation();
      setIsVisible(!isVisible);
    }
  };

  // Handle click outside to close tooltip when on="click"
  useEffect(() => {
    if (on === 'click' && isVisible) {
      const handleClickOutside = (e) => {
        if (triggerRef.current && !triggerRef.current.contains(e.target) &&
            tooltipRef.current && !tooltipRef.current.contains(e.target)) {
          setIsVisible(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [on, isVisible]);

  const tooltipClasses = classNames(
    'ng-ui-tooltip-popup',
    position.replace(' ', '-'),
    {
      'inverted': inverted,
      'visible': isVisible,
    },
    className
  );

  const triggerProps = {
    ref: triggerRef,
    'aria-describedby': isVisible ? tooltipId : undefined,
    ...(on === 'hover' ? {
      onMouseEnter: showTooltip,
      onMouseLeave: hideTooltip,
      onFocus: showTooltip,
      onBlur: hideTooltip,
    } : {
      onClick: handleClick,
    }),
  };

  return (
    <div className="ng-ui-tooltip" {...otherProps}>
      {React.cloneElement(trigger, triggerProps)}
      {isVisible && (
        <div
          ref={tooltipRef}
          id={tooltipId}
          className={tooltipClasses}
          role="tooltip"
          aria-hidden={!isVisible}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="ng-ui-tooltip-arrow" />
          <div className="ng-ui-tooltip-content">{content}</div>
        </div>
      )}
    </div>
  );
};



Tooltip.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  id: PropTypes.string,
  trigger: PropTypes.element.isRequired,
  content: PropTypes.string.isRequired,
  position: PropTypes.oneOf([
    'top right',
    'bottom right',
    'top left',
    'bottom left',
    'right center',
    'left center',
    'top center',
    'bottom center',
  ]),
  on: PropTypes.oneOf([
    'hover',
    'click',
  ]),
  inverted: PropTypes.bool,
};

export default Tooltip;